/**
 * When the Today brief is read again, and what a new read changes on screen
 * (the to-do rebuild, batch 5; design section 6: the app only displays).
 *
 * The server says what a brief was built from and how long it holds: every
 * brief carries `fingerprint` (the garden's version, its ZIP and day, the
 * forecast's stamp, the rules', settings' and words' stamps, the reader's
 * language, unit and days ahead) and `validUntil` (the garden's next
 * midnight). So the app keeps no rebuild marks of its own (the old
 * guidanceRefresh set and briefIsBehind are gone) and never asks for a brief
 * "built afresh": it reads again
 * - when the page comes back into view (React Query's refetch on focus);
 * - after its own writes (gardenWritten: a bed or a plant saved, which reads
 *   the header answer again too; a to-do mark answers with the brief itself);
 * - while the server says it is updating (useTodayBrief's poll);
 * - at validUntil, by a timer (msUntilValid), so a page left open past the
 *   garden's midnight shows the new day by itself.
 * A read whose fingerprint is the one on screen changes nothing on screen
 * (keepSameBrief: the brief held is kept, so nothing redraws).
 *
 * The brief is kept under the garden, not the member's own postcode
 * (briefKey): in a shared garden every member reads the owner's garden, and
 * the owner's new ZIP reaches them on their next read through the server's
 * fingerprint.
 *
 * Imports are relative with their extensions so the Node test runs it:
 *   node --experimental-strip-types --test src/shared/lib/briefFreshness.test.mjs
 *
 * The season brief (features/season useSeason) is kept for minutes (W24 Job
 * H), so gardenWritten marks it stale too, and seasonWritten does that alone
 * for a write that changes only the diary (a photo or a note on a plant or a
 * bed): the next visit to Seasons reads it again, with the new row on it.
 *
 * Where: shared/hooks/useTodayBrief (briefKey, keepSameBrief, msUntilValid),
 * shared/context/LocationsContext and PlantsContext, features/locations
 * LocationGrid (gardenWritten); the photo and note writers (seasonWritten).
 * API: none of its own.
 *
 * @module briefFreshness
 */

import type { QueryClient } from '@tanstack/query-core';
import { PLANT_OUTLOOK_QUERY_KEY } from '../../config/tunables/shared.ts';
import { GARDEN_PLACE_QUERY_KEY } from '../api/gardenPlaceKey.ts';
import { TODAY_BRIEF_QUERY_KEY } from '../../config/tunables/today.ts';

/**
 * The React Query key the header answer (GET /header: the frost bar, the weather now and the pill's icon) is cached
 * under, followed by the garden, the language and the unit (shared/context/WeatherContext; W24 Job D). Here, beside the
 * brief's key, because the two are read again together (gardenWritten) and a value file is not read by new code
 * (scripts/check-value-files).
 * @kept K1
 */
export const HEADER_QUERY_KEY = 'header-answer';

/**
 * The first word of the season brief's query key: the same word as SEASON_QUERY_KEY (config/tunables/seasonReview, which
 * useSeason keys the brief by), written here because this file reads no value file (scripts/check-value-files.mjs).
 * @kept K1
 */
export const SEASON_BRIEF_KEY = 'season';

/** The most a validUntil timer waits, in ms: a day and a bit, so a far-off or wrong stamp still reads again daily. */
export const VALID_UNTIL_MAX_MS = 26 * 60 * 60 * 1000;

/** The least a validUntil timer waits, in ms: a stamp already past reads again soon, not in a tight loop. */
export const VALID_UNTIL_MIN_MS = 5 * 1000;

/** What the freshness helpers read of a brief: its fingerprint, whether the server is updating, its day's end. */
export interface BriefStamp {
  /** What it was built from, hashed; the Today brief carries one, the garden place does not. */
  fingerprint?: string | null;
  /** The server is rebuilding the garden's outlook as it answers. */
  updating?: boolean;
  /** The garden's next midnight, an ISO stamp (the Today brief and the garden place carry one). */
  validUntil?: string | null;
}

/**
 * The React Query key a brief is kept under: the garden, the words' language,
 * the readings' unit and Coming up's days. Not the postcode and not the day:
 * the server's fingerprint says when either changed.
 *
 * @param gardenId - The garden's owner (the person's own id, or the garden they tend).
 * @param lang - The words' language.
 * @param unit - The readings' unit.
 * @param daysAhead - How far ahead Coming up looks; null while the app has not heard the usual number (the server uses its own).
 * @returns The key.
 */
export function briefKey(gardenId: number | null, lang: string, unit: 'F' | 'C', daysAhead: number | null): readonly unknown[] {
  return [TODAY_BRIEF_QUERY_KEY, gardenId, lang, unit, daysAhead];
}

/**
 * The start of the brief's key: every brief (no parts), or the briefs of a garden and a language, for a reader that
 * finds or invalidates briefs it does not read itself (the tips, the header).
 *
 * @param parts - The key's next parts, in order: the garden, the language.
 * @returns [TODAY_BRIEF_QUERY_KEY, ...parts].
 */
export function briefKeyStart(...parts: Array<number | string | null>): readonly unknown[] {
  return [TODAY_BRIEF_QUERY_KEY, ...parts];
}

/**
 * The brief to hold after a read: the one already held when the new one has
 * the same fingerprint and the same updating flag (nothing on screen
 * changes), else the new one. An answer without a fingerprint is always taken.
 *
 * @param held - The brief on screen, or undefined.
 * @param next - The brief just read.
 * @returns Which to hold.
 */
export function keepSameBrief<T extends BriefStamp>(held: T | undefined, next: T): T {
  if (!held || !next?.fingerprint) return next;
  return held.fingerprint === next.fingerprint && Boolean(held.updating) === Boolean(next.updating) ? held : next;
}

/**
 * How long until a brief's day ends (validUntil), for the timer that reads it
 * again; null without a stamp that reads. Never less than VALID_UNTIL_MIN_MS
 * nor more than VALID_UNTIL_MAX_MS.
 *
 * @param validUntil - The brief's validUntil.
 * @param now - The moment, in ms (Date.now()).
 * @returns The wait in ms, or null for no timer.
 */
export function msUntilValid(validUntil: string | null | undefined, now: number): number | null {
  if (!validUntil) return null;
  const at = Date.parse(validUntil);
  if (!Number.isFinite(at)) return null;
  return Math.min(VALID_UNTIL_MAX_MS, Math.max(VALID_UNTIL_MIN_MS, at - now));
}

/**
 * The app wrote something Grow's advice is built from (a bed saved, a plant
 * added, moved or archived): the brief, the outlook (the plants brief is
 * under its key), the garden's place (its `started` ends Today's two start
 * doors, B98), the header answer (its frost bar and pill come from the
 * brief's alert, and a write can end the frost job; W24 Job D) and the season
 * brief (its diary; W24 Job H) are read again by whichever page shows them.
 * The server has
 * already moved the garden's version, so the read is a fresh brief. Fired,
 * not awaited.
 *
 * @param queryClient - The app's client.
 */
export function gardenWritten(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [TODAY_BRIEF_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [PLANT_OUTLOOK_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GARDEN_PLACE_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [HEADER_QUERY_KEY] });
  seasonWritten(queryClient);
}

/**
 * Something the season diary lists was written (a photo or a note on a plant or a bed): the season brief, kept for
 * minutes, is marked stale, so Seasons reads it again on its next visit (at once, if it is open). Nothing else is read.
 * Fired, not awaited.
 *
 * @param queryClient - The app's client.
 */
export function seasonWritten(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [SEASON_BRIEF_KEY] });
}

/**
 * Photos were left on the tray (the photo sheet closed, B78): the Today brief is read again so its "Sort the N photos
 * waiting" job shows at once. Only the brief: nothing the outlook is built from changed. Fired, not awaited.
 *
 * @param queryClient - The app's client.
 */
export function trayChanged(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [TODAY_BRIEF_QUERY_KEY] });
}
